/**
 * InvoiceDetail Page - B2B (Invoice Management)
 * ==============================================
 * Two-column layout: PDF viewer (left) + fields & line items (right).
 * Read-only by default; "Duzenle" button toggles edit mode.
 * Action buttons rendered in Layout headerActions.
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '../../components/layout/Layout';
import { useLang } from '../../shared/i18n';
import documentApi, { DocumentItem } from '../../services/documentApi';
import FieldSection, { FieldDef } from './FieldSection';
import PdfViewerPanel, { EntityWithBounds } from './PdfViewerPanel';
import LineItemsTable, { LineItem } from './LineItemsTable';
import ConfirmModal from './ConfirmModal';
import './InvoiceDetail.scss';

const EXPENSE_CATEGORIES = [
    'food', 'fuel', 'accommodation', 'transport', 'toll',
    'parking', 'office_supplies', 'communication', 'other'
];

/** Flatten extracted_data fields to top-level for editing */
function flattenDoc(doc: DocumentItem): Record<string, any> {
    const ed = doc.extracted_data || {};
    return {
        supplier_name: ed.supplier_name || ed.vendor?.name || '',
        supplier_tax_id: ed.supplier_tax_id || ed.vendor?.tax_id || '',
        supplier_address: ed.supplier_address || ed.vendor?.address || '',
        supplier_email: ed.supplier_email || '',
        supplier_phone: ed.supplier_phone || '',
        supplier_website: ed.supplier_website || '',
        supplier_iban: ed.supplier_iban || '',
        receiver_name: ed.receiver_name || '',
        receiver_address: ed.receiver_address || '',
        total_amount: ed.total_amount ?? ed.financials?.total_amount ?? '',
        net_amount: ed.net_amount ?? ed.financials?.subtotal ?? '',
        total_tax_amount: ed.total_tax_amount ?? ed.financials?.tax ?? '',
        currency: ed.currency || ed.financials?.currency || '',
        expense_category: (doc as any).expense_category || ed.expense_category || '',
        invoice_number: ed.invoice_number || '',
        invoice_type: ed.invoice_type || '',
        invoice_date: ed.invoice_date || '',
        due_date: ed.due_date || '',
    };
}

function getLineItems(doc: DocumentItem): LineItem[] {
    return (doc.extracted_data?.items || doc.extracted_data?.line_items || []) as LineItem[];
}

const InvoiceDetail: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { t } = useLang();

    const dateFields: FieldDef[] = useMemo(() => [
        { key: 'invoice_number', label: t('fieldInvoiceNumber') },
        { key: 'invoice_type', label: t('fieldInvoiceType') },
        { key: 'invoice_date', label: t('fieldInvoiceDate') },
        { key: 'due_date', label: t('fieldDueDate') },
    ], [t]);

    const supplierFields: FieldDef[] = useMemo(() => [
        { key: 'supplier_name', label: t('fieldSupplierName') },
        { key: 'supplier_tax_id', label: t('fieldTaxId') },
        { key: 'supplier_address', label: t('fieldAddress') },
        { key: 'supplier_email', label: t('fieldEmail') },
        { key: 'supplier_phone', label: t('fieldPhone') },
        { key: 'supplier_website', label: t('fieldWebsite') },
        { key: 'supplier_iban', label: t('fieldIban') },
    ], [t]);

    const receiverFields: FieldDef[] = useMemo(() => [
        { key: 'receiver_name', label: t('fieldReceiverName') },
        { key: 'receiver_address', label: t('fieldReceiverAddress') },
    ], [t]);

    const financialFields: FieldDef[] = useMemo(() => [
        { key: 'total_amount', label: t('fieldTotalAmount'), type: 'number' },
        { key: 'net_amount', label: t('fieldNetAmount'), type: 'number' },
        { key: 'total_tax_amount', label: t('fieldTaxAmount'), type: 'number' },
        { key: 'currency', label: t('fieldCurrency') },
        { key: 'expense_category', label: t('fieldCategory'), type: 'select', options: EXPENSE_CATEGORIES },
    ], [t]);

    const [doc, setDoc] = useState<DocumentItem | null>(null);
    const [values, setValues] = useState<Record<string, any>>({});
    const [originalValues, setOriginalValues] = useState<Record<string, any>>({});
    const [lineItems, setLineItems] = useState<LineItem[]>([]);
    const [originalLineItems, setOriginalLineItems] = useState<LineItem[]>([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [editing, setEditing] = useState(false);
    const [highlightsOn, setHighlightsOn] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);

    const fetchDocument = useCallback(async () => {
        if (!id) return;
        setLoading(true);
        try {
            const res = await documentApi.getDocument(id);
            if (res.success && res.data) {
                setDoc(res.data);
                const flat = flattenDoc(res.data);
                const items = getLineItems(res.data);
                setValues(flat);
                setOriginalValues(flat);
                setLineItems(items);
                setOriginalLineItems(items);
            } else {
                setError(res.message || t('invoiceNotFound'));
            }
        } catch {
            setError(t('serverError'));
        } finally {
            setLoading(false);
        }
    }, [id]);

    useEffect(() => { fetchDocument(); }, [fetchDocument]);

    const handleChange = useCallback((key: string, value: any) => {
        setValues(prev => ({ ...prev, [key]: value }));
    }, []);

    const handleCancel = useCallback(() => {
        setValues({ ...originalValues });
        setLineItems([...originalLineItems]);
        setEditing(false);
    }, [originalValues, originalLineItems]);

    const handleSave = useCallback(async () => {
        if (!id) return;
        setSaving(true);
        try {
            const changed: Record<string, any> = {};
            for (const k of Object.keys(values)) {
                if (values[k] !== originalValues[k]) {
                    const apiKey = k === 'supplier_name' ? 'vendor_name' : k;
                    changed[apiKey] = values[k];
                }
            }
            if (JSON.stringify(lineItems) !== JSON.stringify(originalLineItems)) {
                changed['line_items'] = lineItems;
            }
            if (Object.keys(changed).length > 0) {
                await documentApi.updateDocumentFields(id, changed);
                setOriginalValues({ ...values });
                setOriginalLineItems([...lineItems]);
            }
            setEditing(false);
        } catch {
            alert(t('saveError'));
        } finally {
            setSaving(false);
        }
    }, [id, values, originalValues, lineItems, originalLineItems]);

    const handleDeleteConfirm = useCallback(async () => {
        if (!id) return;
        setShowDeleteModal(false);
        try {
            await documentApi.deleteDocument(id);
            navigate('/invoices');
        } catch {
            alert(t('deleteErrorDetail'));
        }
    }, [id, navigate]);

    const headerActions = useMemo(() => (
        <>
            <button
                className="header-action-btn"
                onClick={() => navigate('/invoices')}
            >
                &#x2190; {t('backToList')}
            </button>
            <div className="header-actions">
                <button
                    className={`action-button ${highlightsOn ? 'action-button-primary' : ''}`}
                    onClick={() => setHighlightsOn(prev => !prev)}
                >
                    {highlightsOn ? t('highlightOn') : t('highlightOff')}
                </button>
                {editing ? (
                    <>
                        <button className="action-button" onClick={handleCancel}>
                            {t('cancel')}
                        </button>
                        <button
                            className="action-button action-button-primary"
                            onClick={handleSave}
                            disabled={saving}
                        >
                            {saving ? t('saving') : t('save')}
                        </button>
                    </>
                ) : (
                    <>
                        <button className="action-button" onClick={() => setEditing(true)}>
                            {t('edit')}
                        </button>
                        <button
                            className="action-button action-button-danger"
                            onClick={() => setShowDeleteModal(true)}
                        >
                            {t('delete')}
                        </button>
                    </>
                )}
            </div>
        </>
    ), [highlightsOn, editing, saving, handleCancel, handleSave, navigate, t]);

    const pageDescription = useMemo(() => {
        if (!doc) return undefined;
        const date = doc.created_at
            ? new Date(doc.created_at).toLocaleDateString('tr-TR')
            : '';
        return date ? `${doc.filename}  ·  ${date}` : doc.filename;
    }, [doc]);

    const entities: EntityWithBounds[] = useMemo(() => {
        return (doc?.extracted_data?.entities_with_bounds || []) as EntityWithBounds[];
    }, [doc]);

    const initialPage = useMemo(() => {
        const md = (doc as any)?.multi_document;
        if (!md?.boundaries?.length) return undefined;
        const b = md.boundaries[0];
        return b.pages ? b.pages[0] : b.page;
    }, [doc]);

    if (loading) {
        return (
            <Layout pageTitle={t('invoiceDetailTitle')} pageDescription={pageDescription} headerActions={headerActions}>
                <div className="invoice-detail__loading">{t('loading')}</div>
            </Layout>
        );
    }

    if (error || !doc) {
        return (
            <Layout pageTitle={t('invoiceDetailTitle')} pageDescription={pageDescription} headerActions={headerActions}>
                <div className="invoice-detail__error">{error || t('invoiceNotFound')}</div>
            </Layout>
        );
    }

    return (
        <Layout pageTitle={t('invoiceDetailTitle')} pageDescription={pageDescription} headerActions={headerActions}>
            <div className="invoice-detail">
                <div className="invoice-detail__body">
                    <div className="invoice-detail__pdf-col">
                        <PdfViewerPanel
                            documentId={id!}
                            parentDocumentId={(doc as any).parent_document_id}
                            entities={entities}
                            showHighlights={highlightsOn}
                            initialPage={initialPage}
                        />
                    </div>

                    <div className="invoice-detail__fields-col">
                        <FieldSection title={t('sectionInvoiceInfo')} fields={dateFields} values={values} onChange={handleChange} readOnly={!editing} />
                        <FieldSection title={t('sectionSupplier')} fields={supplierFields} values={values} onChange={handleChange} readOnly={!editing} />
                        <FieldSection title={t('sectionReceiver')} fields={receiverFields} values={values} onChange={handleChange} readOnly={!editing} />
                        <FieldSection title={t('sectionFinancial')} fields={financialFields} values={values} onChange={handleChange} readOnly={!editing} />
                        <LineItemsTable items={lineItems} editing={editing} onChange={setLineItems} />
                    </div>
                </div>
            </div>

            <ConfirmModal
                isOpen={showDeleteModal}
                title={t('deleteTitle')}
                message={t('deleteMessage')}
                confirmLabel={t('deleteConfirmLabel')}
                cancelLabel={t('deleteCancelLabel')}
                variant="danger"
                onConfirm={handleDeleteConfirm}
                onCancel={() => setShowDeleteModal(false)}
            />
        </Layout>
    );
};

export default InvoiceDetail;
